import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as mqtt from "precompiled-mqtt";
import { IMQTTBrokerSettings } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import {
    CHECK_LOCALSERVER_CONNECTIVITY_INTERVAL,
    CHECK_RESPONSE_TIMEOUT_LOCALSERVER_INTERVAL,
    KEEP_ALIVE_TOPIC,
    MQTTEventsEnum,
} from "constants/constants";
import { uniqueClientId } from "mqtt/uniqueClientId";

const { connect } = mqtt;

const MIN_RATE_ACTIVE = Math.max(CHECK_LOCALSERVER_CONNECTIVITY_INTERVAL, 5000);
const MIN_RATE_INACTIVE = Math.max(CHECK_LOCALSERVER_CONNECTIVITY_INTERVAL / 5, 5000);

function useMQTTKeepAlive(dedicatedLocalserver: boolean, options?: IMQTTBrokerSettings, code?: string) {
    const [isLocalServerActive, setIsLocalServerActive] = useState(false);
    const [isBrokerConnected, setIsBrokerConnected] = useState(false);
    // Guards against overlapping checks: the worst-case check budget (~6s) is about the same
    // as MIN_RATE_INACTIVE, so without this the next tick can start before the previous check
    // gives up, leaving two clients live at once. Skip a tick while one is still in flight.
    const checkInFlightRef = useRef(false);

    const keepAliveInterval = useMemo(
        () => (!isBrokerConnected || !isLocalServerActive ? MIN_RATE_INACTIVE : MIN_RATE_ACTIVE),
        [isBrokerConnected, isLocalServerActive],
    );

    const checkMQTT = useCallback((options: IMQTTBrokerSettings, code: string) => {
        return new Promise((resolve, reject) => {
            try {
                const mosquittoUrl = options.host;
                let connectionHandled = false;
                const isWSS = mosquittoUrl.startsWith("wss");
                const client = connect(mosquittoUrl, {
                    username: options.user,
                    password: options.password,
                    protocol: isWSS ? "wss" : "ws",
                    clean: true,
                    clientId: uniqueClientId(`${code}-ka`),
                    reconnectPeriod: 0,
                    connectTimeout: CHECK_RESPONSE_TIMEOUT_LOCALSERVER_INTERVAL,
                });

                // One-shot teardown gate: every terminal path (success, error,
                // close, timeout) must route through settle() so no path can
                // ever leak a live client.
                let settled = false;
                const settle = () => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    clearTimeout(safetyTimeout);
                    client.end(true);
                };

                // Safety net: even if no connect/error/close event ever fires,
                // the client is force-closed shortly after the connect timeout.
                const safetyTimeout = setTimeout(() => {
                    settle();
                    setIsBrokerConnected(false);
                    setIsLocalServerActive(false);
                    reject(new Error("Keep-alive check timed out"));
                }, CHECK_RESPONSE_TIMEOUT_LOCALSERVER_INTERVAL + 1000);

                const handleError = (err: Error) => {
                    connectionHandled = true;
                    settle();
                    setIsBrokerConnected(false);
                    setIsLocalServerActive(false);
                    reject(err);
                };

                const handleConnect = () => {
                    if (connectionHandled) {
                        return;
                    }
                    connectionHandled = true;
                    setIsBrokerConnected(true);
                    client.subscribe({ [KEEP_ALIVE_TOPIC]: { qos: 1 } }, (err) => {
                        if (err) {
                            settle();
                            // Connected but couldn't subscribe: the check failed, so clear the
                            // active flag (consistent with handleError/handleClose/safety timeout)
                            // instead of leaving a stale "active" from a previous cycle.
                            setIsLocalServerActive(false);
                            reject(new Error(`Failed to subscribe to ${KEEP_ALIVE_TOPIC}`));
                            return;
                        }
                    });
                };

                const handleMessage = (topic: string, message: Buffer) => {
                    connectionHandled = true;
                    if (topic === KEEP_ALIVE_TOPIC) {
                        try {
                            const response = JSON.parse(message.toString()) as {
                                lastCloudSync: Date;
                                serverTime: number;
                            };

                            if (response.serverTime) {
                                const currentClientTime = Date.now();
                                const timeDifference = Math.abs(currentClientTime - response.serverTime);
                                const isTimeInSync = timeDifference < 60000;

                                if (isTimeInSync) {
                                    settle();
                                    setIsLocalServerActive(true);
                                    resolve(response);
                                } else {
                                    settle();
                                    setIsLocalServerActive(false);
                                    reject(new Error("Server time is out of sync by more than 1 minute"));
                                }
                            } else {
                                settle();
                                setIsLocalServerActive(false);
                                reject(new Error("Server response without timestamp"));
                            }
                        } catch (error) {
                            settle();
                            setIsLocalServerActive(false);
                            reject(new Error("Failed to parse response"));
                        }
                    }
                };

                const handleClose = () => {
                    // Always tear down on close — with reconnectPeriod 0 a
                    // closed client is dead either way; ending it releases the
                    // socket and the safety timer.
                    settle();
                    if (!connectionHandled) {
                        connectionHandled = true;
                        setIsBrokerConnected(false);
                        setIsLocalServerActive(false);
                    }
                    // Always settle the promise (reject is a no-op once resolved) so a close that
                    // arrives after CONNECT but before the check resolves can't leave it pending —
                    // which would latch the caller's in-flight guard and stall all future checks.
                    reject(new Error("Keep-alive connection closed"));
                };

                client.on(MQTTEventsEnum.ERROR, handleError);

                client.on(MQTTEventsEnum.CONNECT, handleConnect);

                client.on(MQTTEventsEnum.MESSAGE, handleMessage);

                client.on(MQTTEventsEnum.CLOSE, handleClose);
            } catch (error) {
                setIsBrokerConnected(false);
                setIsLocalServerActive(false);
                // Settle so a synchronous throw (e.g. connect() or a malformed host) can't leave
                // the promise pending and latch the caller's in-flight guard forever.
                reject(error as Error);
            }
        });
    }, []);

    useEffect(() => {
        if (options && code && dedicatedLocalserver) {
            const runCheck = () => {
                // Skip if a previous check hasn't settled yet, so we never hold two clients at
                // once (see checkInFlightRef).
                if (checkInFlightRef.current) {
                    return;
                }
                checkInFlightRef.current = true;
                // Rejections are the expected "server not reachable" signal — the
                // state setters already captured them; avoid unhandled-rejection
                // noise on every failed check.
                checkMQTT(options, code)
                    .catch(() => undefined)
                    .finally(() => {
                        checkInFlightRef.current = false;
                    });
            };

            runCheck();

            const timerId = setInterval(runCheck, keepAliveInterval);

            return () => clearInterval(timerId);
        } else {
            setIsBrokerConnected(!dedicatedLocalserver);
            setIsLocalServerActive(!dedicatedLocalserver);
        }
    }, [options, code, keepAliveInterval, checkMQTT, dedicatedLocalserver]);

    return { isLocalServerActive, isBrokerConnected };
}

export default useMQTTKeepAlive;
